import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { Dropdown } from 'semantic-ui-react';
import * as _ from 'es-toolkit';

import './SelectField.css';

function optionListIncludesValue(opts, val) {
    if (val === '' || val == null)
        return true;
    if (!opts || !opts.length)
        return false;
    return opts.some(o => {
        if (Object.is(o.value, val))
            return true;
        if (String(o.value) === String(val))
            return true;
        const n = Number(val);
        return Number.isFinite(n) && Number(o.value) === n;
    });
}

/**
 * Redux-form field renderer: keep at module scope so Field does not remount every parent render.
 * Options come from Field `props` (not closure) so lists stay in sync when options change.
 * Controlled `value` + selectOnBlur={false} avoid Semantic UI search dropdowns committing the wrong
 * option on blur / tab (which could write e.g. a cinema name into another field’s redux value).
 */
function SelectFieldDropdown(props) {
    const {
        input,
        options,
        placeholder,
        fixedWidth,
        selectOnBlur = false,
        className,
        onChange: _fieldOnChange,
        ...rest
    } = props;
    const { t } = useTranslation();

    const onChange = (e, data) => {
        const next = data.value;
        const is_empty = next === '' || next == null;
        if (is_empty && input.value) {
            const node = e?.currentTarget || e?.target;
            if (node && typeof node.closest === 'function') {
                // Ignore clear-on-blur while the control lives in a hidden tab panel
                // (Bootstrap .tab-pane, or Kronos d-none panels that stay mounted).
                const pane = node.closest('.tab-pane');
                if (pane && !pane.classList.contains('active'))
                    return;
                if (node.closest('.d-none'))
                    return;
            }
        }
        input.onChange(next);
    };

    const opts = options || [];
    let dropdownOptions = opts;
    const v = input.value;
    if (v !== '' && v != null && !optionListIncludesValue(opts, v)) {
        dropdownOptions = opts.concat(
            _.difference([v], opts.map(o => o.value)).map(id => ({
                key: id,
                text: `[${t('common.unknown')} - ${id}]`,
                className: 'line-through',
                value: id,
            }))
        );
    }

    const valueProp = v === '' || v == null ? undefined : v;

    return (
        <Dropdown
            name={input.name}
            options={dropdownOptions}
            value={valueProp}
            onChange={onChange}
            placeholder={placeholder}
            fluid={!fixedWidth || fixedWidth === 'undefined'}
            search
            selection
            scrolling
            clearable
            selectOnBlur={selectOnBlur}
            className={className}
            {...rest}
        />
    );
}

const SelectField = ({ name, input, meta: _meta, options, placeholder, onSelectChange, format, parse, fixedWidth, noField, value, ...rest }) => {
    const [standalone_value, set_standalone_value] = useState(undefined);

    if (noField) {
        const is_controlled = value !== undefined;
        const current = is_controlled ? value : standalone_value;
        const onChange = (e, data) => {
            if (!is_controlled)
                set_standalone_value(data.value);
            onSelectChange?.(data.value);
        };
        // Keep '' (not undefined) so Semantic clears the visible label when controlled.
        const dropdown_value = current == null ? '' : current;

        return (
            <div className="SelectField">
                <Dropdown
                    options={options || []}
                    value={dropdown_value}
                    onChange={onChange}
                    placeholder={placeholder}
                    fluid={!fixedWidth || fixedWidth === 'undefined'}
                    search
                    selection
                    scrolling
                    clearable
                    selectOnBlur={false}
                    className="form-control"
                    {...rest}
                />
            </div>
        );
    }

    const field_name = name ?? input?.name;
    return (
        <div className="SelectField">
            <Field
                name={field_name}
                component={SelectFieldDropdown}
                format={format}
                parse={parse}
                props={{ options, placeholder, fixedWidth, ...rest }}
                onChange={onSelectChange}
                className="form-control"
            />
        </div>
    );
};

export default SelectField;
